/* ============================================================
   CRO Sprint 2026-07-03 — variant picker clarity (add-on)
   Loaded by snippets/product-options.liquid alongside
   cro-variant-ux.css. Pure enhancement: reads the variant JSON
   Venue already embeds, never modifies vendor code
   (product-interactive-options.js / variant-select-components.js).

   Fixes two confirmed conversion bugs on multi-option PDPs
   (Certification × Frame Colour):
   1. Swatches for combos that don't exist were display:none'd —
      customers thought the colour didn't exist. We tag them
      `.cro-missing` (CSS un-hides + dims) with a tooltip, and a
      tap explains how to get that colour instead of doing nothing.
   2. Changing certification silently jumped the frame colour
      (vendor "first existing variant" fallback). We can't stop
      the jump without forking vendor code, so we announce it:
      "<colour> isn't available in <cert> — switched to <colour>."

   Scope: only pickers inside <product-single> (the main PDP form),
   so quick-shop cards and sticky bars keep stock behaviour.

   2026-10-26 (ported from the Venue 18 PDP): lens tiles follow the
   frame colour. Lenses not made or in stock in the chosen colour
   are hidden and an empty lens row loses its heading; see
   updateLensLinks().
   ============================================================ */
(() => {
  const PICKER_SELECTOR = 'variant-radios, variant-mixed-inputs, variant-selects';
  const TILE_LINK_SELECTOR = 'a.hys-lens-opt, a.hys-frclr-opt';

  /* "M. Black" on some lens products is "M.Black" on others. */
  const valueKey = (s) => String(s == null ? '' : s).toLowerCase().replace(/[^a-z0-9]/g, '');
  const sameValue = (a, b) => valueKey(a) === valueKey(b);
  /* By handle: a product reached from a collection sits at
     /collections/<c>/products/<handle>. */
  const handleOf = (path) => (String(path).match(/\/products\/([^/?#]+)/) || [])[1] || null;

  /* The active main-PDP picker; lens tiles live outside the picker
     element, so tile clicks are handled at document level. */
  let activePicker = null;

  /* Same-product lens tiles (REVS SIGMA, FLYERS Polarized) switch
     the variant in place — no page reload. Cross-product tiles
     still navigate (different price/copy/reviews) but are
     prefetched on hover/touch below, so the hop is near-instant. */
  document.addEventListener('click', (e) => {
    if (!activePicker) return;
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const link = e.target.closest('a.hys-lens-opt');
    if (!link) return;
    if (activePicker.selectTileVariant(link)) e.preventDefault();
  });

  const setupPrefetch = () => {
    if (window.__croTilePrefetch) return;
    window.__croTilePrefetch = true;
    if (HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules')) {
      const rules = document.createElement('script');
      rules.type = 'speculationrules';
      rules.textContent = JSON.stringify({
        prefetch: [{ source: 'document', where: { selector_matches: TILE_LINK_SELECTOR }, eagerness: 'moderate' }],
      });
      document.head.appendChild(rules);
      return;
    }
    // Fallback: <link rel="prefetch"> on first hover/touch.
    const seen = new Set();
    const prefetch = (e) => {
      const link = e.target.closest && e.target.closest(TILE_LINK_SELECTOR);
      if (!link || !link.href || seen.has(link.href)) return;
      seen.add(link.href);
      const hint = document.createElement('link');
      hint.rel = 'prefetch';
      hint.href = link.href;
      hint.as = 'document';
      document.head.appendChild(hint);
    };
    document.addEventListener('pointerover', prefetch, true);
    document.addEventListener('touchstart', prefetch, { capture: true, passive: true });
  };

  class CroVariantUX {
    constructor(root) {
      this.root = root;
      const json = root.querySelector('script[type="application/json"]');
      if (!json) return;
      try {
        this.variants = JSON.parse(json.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(this.variants) || this.variants.length < 2) return;

      activePicker = this;
      this.notice = this.buildNotice();
      this.preClick = null;
      // Option groups hidden on this PDP (Rx: Certification). Chosen
      // automatically from the visible groups; see autoSelect().
      this.autoGroups = (root.dataset.croAutoOptions || '')
        .split('|')
        .map((s) => s.trim())
        .filter(Boolean);

      // Capture-phase pointerdown: snapshot selection before the
      // vendor click handler mutates it, so we can detect side
      // effects (another option group changing value).
      root.addEventListener('pointerdown', (e) => this.onPointerDown(e), true);
      // Vendor re-dispatches change after every user selection.
      root.addEventListener('change', () => this.refresh());
      // Clicks on missing combos never fire change; explain them.
      root.addEventListener('click', (e) => this.onClick(e), true);

      // Vendor availability pass runs in a setTimeout on connect;
      // run ours after it, then keep in sync via events above.
      setTimeout(() => this.refresh(), 50);
    }

    inputs() {
      return Array.from(this.root.querySelectorAll('input[type="radio"]'));
    }

    /* Ordered option-group names, matching variant.options order. */
    groups() {
      const names = [];
      this.inputs().forEach((i) => {
        if (!names.includes(i.name)) names.push(i.name);
      });
      return names;
    }

    /* Current selection as { groupName: value }. */
    selection() {
      const sel = {};
      this.root.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
        sel[i.name] = i.value;
      });
      this.root.querySelectorAll('select').forEach((s) => {
        const name = s.name.replace('options[', '').replace(']', '');
        sel[name] = s.value;
      });
      return sel;
    }

    variantMatching(sel) {
      const groups = this.groups();
      return this.variants.find((v) =>
        groups.every((g, idx) => v.options[idx] === sel[g])
      ) || null;
    }

    labelFor(input) {
      return this.root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
    }

    /* Best variant for a visible swatch when some groups are hidden:
       the swatch's value plus the current visible choices, with any
       hidden-group value. Prefers in stock, then the current hidden
       value. Null when the swatch has no variant at all. */
    bestVariantFor(input) {
      const groups = this.groups();
      const sel = this.selection();
      const idx = groups.indexOf(input.name);
      const pool = this.variants.filter((v) =>
        groups.every((g, i) => {
          if (i === idx) return v.options[i] === input.value;
          return this.autoGroups.includes(g) || v.options[i] === sel[g];
        })
      );
      const score = (v) =>
        (v.available ? 2 : 0) +
        (groups.every((g, i) => i === idx || v.options[i] === sel[g]) ? 1 : 0);
      return pool.reduce((best, v) => (!best || score(v) > score(best) ? v : best), null);
    }

    /* Hidden-group PDPs: a swatch tap selects bestVariantFor() through
       the vendor's own selectVariantById (sets every group, availability,
       price, media, URL and the form's variant id), so a colour that
       only exists in the other certification (Slim Lilac) is reachable
       and a colour sold out in one certification lands on the in-stock
       one. Returns true when it handled the tap. */
    autoSelect(e) {
      const label = e.target.closest('label');
      const input = label
        ? document.getElementById(label.getAttribute('for'))
        : e.target.closest('input[type="radio"]');
      if (!input || !this.root.contains(input)) return false;
      if (this.autoGroups.includes(input.name)) return false;
      const options = this.root.querySelector('product-interactive-options');
      if (!options || typeof options.selectVariantById !== 'function') return false;
      const target = this.bestVariantFor(input);
      if (!target) return false;
      e.preventDefault();
      e.stopPropagation();
      this.preClick = null;
      try {
        options.selectVariantById(target.id);
      } catch (err) {
        // Venue's gallery can throw (null slider) after the variant,
        // URL and label are already applied; see hys-ansi-clearance.js.
        console.warn(err);
      }
      this.refresh();
      return true;
    }

    /* Tag each swatch: missing combo vs sold out, with tooltips. */
    refresh() {
      const sel = this.selection();
      const auto = this.autoGroups.length > 0;
      this.inputs().forEach((input) => {
        const label = this.labelFor(input);
        if (!label) return;
        if (auto && this.autoGroups.includes(input.name)) return;
        let variant;
        if (auto) {
          variant = this.bestVariantFor(input);
          // Vendor disables swatches outside the current (hidden)
          // certification; re-enable any colour that has a variant.
          if (variant) {
            input.disabled = false;
            if (variant.available) label.classList.remove('is-disabled');
          }
        } else {
          const candidate = Object.assign({}, sel);
          candidate[input.name] = input.value;
          variant = this.variantMatching(candidate);
        }
        label.classList.remove('cro-missing', 'cro-soldout');
        label.removeAttribute('title');
        if (!variant) {
          const context = Object.keys(sel)
            .filter((g) => g !== input.name)
            .map((g) => sel[g])
            .join(' / ');
          label.classList.add('cro-missing');
          label.title = `${input.value} isn’t available in ${context}`;
        } else if (!variant.available) {
          label.classList.add('cro-soldout');
          label.title = `${input.value} — sold out`;
        }
      });
      this.detectJump();
      this.updateLensLinks(sel);
      this.syncLensTiles(sel);
    }

    /* Lens-first products (REVS SIGMA 8K, FLYERS Polarized): the
       hys-style-buttons lens tiles reference VARIANTS of this same
       product — the lens is decided by the frame (Sahara=Red,
       Anvil=Blue; Clear=Green Polarized, etc.). Stock behaviour
       showed every frame regardless of the chosen lens, so tapping
       a frame silently changed the lens. Here: only frames paired
       with the selected lens stay visible, the active tile
       highlight follows the live selection, and the "Lens
       Selected" label stays accurate. Products whose tiles all
       point to other products (ICON etc.) are untouched. */
    syncLensTiles(sel) {
      const productPath = (this.root.dataset.url || location.pathname).split('?')[0];
      const tiles = Array.from(document.querySelectorAll('a.hys-lens-opt'))
        .map((a) => {
          const href = a.getAttribute('href') || '';
          const path = href.split('?')[0];
          const match = href.match(/[?&]variant=(\d+)/);
          return { a, path, vid: match ? Number(match[1]) : null };
        })
        .filter((t) => t.vid && t.path === productPath);
      if (!tiles.length) return;

      const groups = this.groups();
      let frameIdx = groups.findIndex((g) => /colou?r/i.test(g));
      if (frameIdx < 0) frameIdx = groups.length - 1;
      const frameGroup = groups[frameIdx];

      tiles.forEach((t) => {
        const v = this.variants.find((vv) => vv.id === t.vid);
        t.frame = v ? v.options[frameIdx] : null;
      });

      const activeTile = tiles.find((t) => t.frame === sel[frameGroup]);
      if (!activeTile) return;

      tiles.forEach((t) => t.a.classList.toggle('rdc-active', t === activeTile));
      const lensLabel = activeTile.a.closest('.hys-lens-main')
        ?.querySelector('.hys-style-opts-label-v');
      if (lensLabel && activeTile.a.dataset.lens) {
        lensLabel.textContent = activeTile.a.dataset.lens;
      }

      // Hide frames that belong to a different lens tile; frames
      // with no tile mapping stay visible so nothing is orphaned.
      const tileFrames = new Set(tiles.map((t) => t.frame).filter(Boolean));
      this.inputs().forEach((input) => {
        if (input.name !== frameGroup) return;
        const item = input.closest('.product-form__swatch__item');
        if (!item) return;
        const hide = tileFrames.has(input.value) && input.value !== activeTile.frame;
        item.classList.toggle('cro-lens-hidden', hide);
      });
    }

    /* Tile click → in-place variant switch when the tile references
       a variant of THIS product. Keeps the shopper's other choices
       (e.g. certification) when that combination exists, otherwise
       takes the tile's exact variant. Returns false to allow normal
       navigation (cross-product tiles, unknown variants). */
    selectTileVariant(link) {
      if (!this.inputs().length) return false;
      const productPath = (this.root.dataset.url || location.pathname).split('?')[0];
      const href = link.getAttribute('href') || '';
      if (href.split('?')[0] !== productPath) return false;
      const match = href.match(/[?&]variant=(\d+)/);
      if (!match) return false;
      const tileVariant = this.variants.find((v) => v.id === Number(match[1]));
      if (!tileVariant) return false;

      const groups = this.groups();
      let frameIdx = groups.findIndex((g) => /colou?r/i.test(g));
      if (frameIdx < 0) frameIdx = groups.length - 1;
      const sel = this.selection();
      const desired = groups.map((g, i) =>
        i === frameIdx ? tileVariant.options[i] : (sel[g] ?? tileVariant.options[i])
      );
      const target =
        this.variants.find((v) => desired.every((o, i) => v.options[i] === o)) ||
        tileVariant;

      let changed = false;
      groups.forEach((g, i) => {
        const input = this.inputs().find(
          (inp) => inp.name === g && inp.value === target.options[i]
        );
        if (input && !input.checked) {
          input.checked = true;
          changed = true;
        }
      });
      if (changed) this.root.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    /* The shopper's frame colour, or null when no option group is
       a colour (then no lens is hidden). */
    frameColour(sel) {
      const group = this.groups().find((g) => /colou?r/i.test(g));
      return group ? sel[group] || null : null;
    }

    /* A lens tile's variant map (snippets/cro-lens-variant-map),
       parsed once; null for an empty map. A variant tile on a product
       whose frame decides the lens (REVS SIGMA 8K: Sahara is Red,
       Anvil is Blue) keeps only its own lens's frame colours, in any
       certification. */
    tileVariants(link) {
      if (!this.tileCache) this.tileCache = new WeakMap();
      if (this.tileCache.has(link)) return this.tileCache.get(link);
      let variants = null;
      try {
        variants = JSON.parse(link.dataset.croVariants);
      } catch (e) {
        variants = null;
      }
      if (!Array.isArray(variants) || !variants.length) {
        variants = null;
      } else {
        const ref = variants.find((v) => v.ref);
        const lenses = variants
          .map((v) => v.lens)
          .filter((l, i, all) => l && all.findIndex((x) => sameValue(x, l)) === i);
        if (ref && ref.lens && lenses.length > 1) {
          const colours = variants.filter((v) => sameValue(v.lens, ref.lens)).map((v) => v.colour);
          variants = variants.filter((v) => colours.some((c) => sameValue(c, v.colour)));
        }
      }
      this.tileCache.set(link, variants);
      return variants;
    }

    /* Deep-link the hys-style-buttons lens switcher to the sibling
       product's variant matching the current colour + cert, so
       switching lenses keeps the shopper's frame colour. Links
       without a match (or without a data map) stay stock.
       2026-10-26: only lenses made AND in stock in the chosen colour
       show (Peter, same rule as the Venue 18 PDP); the page's own
       lens always stays, and a lens row left empty loses its
       heading. Hidden, not dimmed: dimmed tiles read as sold out.
       Tiles with an empty map (same-product REVS SIGMA / FLYERS
       Polarized tiles, handled by syncLensTiles) are never hidden. */
    updateLensLinks(sel) {
      const values = Object.values(sel);
      const colour = this.frameColour(sel);
      const pageHandle = handleOf(this.root.dataset.url || location.pathname);
      document
        .querySelectorAll('a.hys-lens-opt[data-cro-variants]')
        .forEach((link) => {
          const variants = this.tileVariants(link);
          if (!variants) return;
          const base = link.getAttribute('href').split('?')[0];
          const own = handleOf(base) === pageHandle;
          // A map without colours (a page cached from before
          // 2026-10-26) keeps the old link rewrite and hides nothing.
          const known = variants.every((v) => 'colour' in v);
          const inColour = colour && known ? variants.filter((v) => sameValue(v.colour, colour)) : [];
          const inStock = inColour.filter((v) => v.available);
          (link.closest('.hys-lens-box') || link).classList.toggle(
            'cro-lens-off',
            known && !!colour && !own && !inStock.length
          );
          // Best match: most current option values shared, prefer
          // in-stock. Works across 1-option (REVS) and 2-option
          // (Certification × Colour) siblings. Lenses that come in
          // the chosen colour pick from that colour only (in stock
          // first; the page's own lens may be sold out in it).
          const pool = inStock.length ? inStock : own && inColour.length ? inColour : variants;
          let best = null;
          let bestScore = 0;
          pool.forEach((v) => {
            const score =
              v.options.filter((o) => values.includes(o)).length +
              (v.available ? 0.5 : 0);
            if (score > bestScore) {
              best = v;
              bestScore = score;
            }
          });
          if (pool !== variants && !best) best = pool[0];
          // Only rewrite when at least one real option value matched.
          if (!best || (pool === variants && bestScore < 1)) return;
          link.setAttribute('href', `${base}?variant=${best.id}`);
        });
      document.querySelectorAll('.hys-lens-inner').forEach((row) => {
        const empty = !row.querySelector('.hys-lens-box:not(.cro-lens-off)');
        row.classList.toggle('cro-lens-off', empty);
        const heading = row.previousElementSibling;
        if (heading && heading.classList.contains('hys-style-opts-subl')) {
          heading.classList.toggle('cro-lens-off', empty);
        }
      });
    }

    onPointerDown(e) {
      const label = e.target.closest('label');
      const input = label
        ? document.getElementById(label.getAttribute('for'))
        : e.target.closest('input[type="radio"]');
      if (!input || !this.root.contains(input)) return;
      this.preClick = {
        selection: this.selection(),
        group: input.name,
        value: input.value,
      };
    }

    /* After a change, if a group the user did NOT click moved,
       the vendor fallback jumped it — announce what happened. */
    detectJump() {
      const pre = this.preClick;
      this.preClick = null;
      if (!pre) return;
      const now = this.selection();
      const jumped = Object.keys(now).filter(
        (g) => g !== pre.group && !this.autoGroups.includes(g) && pre.selection[g] !== now[g]
      );
      if (!jumped.length) return;
      const parts = jumped.map(
        (g) => `${pre.selection[g]} isn’t available in ${pre.value} — switched to ${now[g]}`
      );
      this.showNotice(`${parts.join('. ')}.`);
    }

    /* Tapping a missing swatch does nothing in stock Venue; tell
       the customer why and how to get that combination. Only for
       inert (disabled) inputs — enabled inputs that jump another
       option are announced by detectJump() instead. */
    onClick(e) {
      if (this.autoGroups.length && this.autoSelect(e)) return;
      const label = e.target.closest('label.cro-missing');
      if (!label || !this.root.contains(label)) return;
      const input = document.getElementById(label.getAttribute('for'));
      if (!input || !input.disabled) return;
      const groups = this.groups();
      const idx = groups.indexOf(input.name);
      const withValue = this.variants.find((v) => v.options[idx] === input.value);
      let hint = '';
      if (withValue) {
        const alt = groups
          .filter((g) => g !== input.name)
          .map((g) => withValue.options[groups.indexOf(g)])
          .join(' / ');
        hint = ` Available in ${alt}.`;
      }
      const context = groups
        .filter((g) => g !== input.name)
        .map((g) => this.selection()[g])
        .join(' / ');
      this.showNotice(`${input.value} isn’t available in ${context}.${hint}`);
    }

    buildNotice() {
      const el = document.createElement('div');
      el.className = 'cro-variant-notice';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.hidden = true;
      const options = this.root.querySelector('product-interactive-options');
      (options || this.root).appendChild(el);
      return el;
    }

    showNotice(text) {
      if (!this.notice) return;
      this.notice.textContent = text;
      this.notice.hidden = false;
      clearTimeout(this.noticeTimer);
      this.noticeTimer = setTimeout(() => {
        this.notice.hidden = true;
      }, 8000);
    }
  }

  /* Hidden option groups on Venue 18's picker (no
     product-interactive-options; CroVariantUX handles the older one).
     Venue 18 resolves the variant server-side from the checked radios,
     so before a colour tap lands we check the hidden value that gives
     that colour its best variant (in stock first, then the current
     value), and keep colours that exist under another hidden value
     from looking unavailable after each re-render. */
  const AUTO_SELECTOR = 'variant-radios[data-cro-auto-options]';
  const autoRoot = (el) => {
    const root = el && el.closest ? el.closest(AUTO_SELECTOR) : null;
    if (!root || root.querySelector('product-interactive-options')) return null;
    return root;
  };
  const autoState = (root) => {
    const json = root.querySelector('script[id^="all-variant-data-"]');
    if (!json) return null;
    let variants;
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants)) return null;
    const groups = Array.from(root.querySelectorAll('fieldset')).map(
      (fs) => fs.querySelector('input[type="radio"]')?.name
    );
    const hidden = root.dataset.croAutoOptions.split('|').map((s) => s.trim());
    const sel = {};
    root.querySelectorAll('input[type="radio"]:checked').forEach((i) => {
      sel[i.name] = i.value;
    });
    return { variants, groups, hidden, sel };
  };
  const autoBest = (state, input) => {
    const { variants, groups, hidden, sel } = state;
    const idx = groups.indexOf(input.name);
    const pool = variants.filter((v) =>
      groups.every((g, i) =>
        i === idx ? v.options[i] === input.value : hidden.includes(g) || v.options[i] === sel[g]
      )
    );
    const score = (v) =>
      (v.available ? 2 : 0) +
      (groups.every((g, i) => i === idx || v.options[i] === sel[g]) ? 1 : 0);
    return pool.reduce((best, v) => (!best || score(v) > score(best) ? v : best), null);
  };
  const autoSyncLabels = (root) => {
    const state = autoState(root);
    if (!state) return;
    root.querySelectorAll('input[type="radio"]').forEach((input) => {
      if (state.hidden.includes(input.name)) return;
      const label = root.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      if (!label) return;
      const best = autoBest(state, input);
      const off = !(best && best.available);
      if (label.classList.contains('is-disabled') !== off) label.classList.toggle('is-disabled', off);
    });
  };
  const autoInit = () => {
    document.querySelectorAll(AUTO_SELECTOR).forEach((root) => {
      if (!autoRoot(root) || root.dataset.croAutoInit) return;
      root.dataset.croAutoInit = 'true';
      autoSyncLabels(root);
      new MutationObserver(() => autoSyncLabels(root)).observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['class', 'checked'],
      });
    });
    if (window.__croAutoClick) return;
    window.__croAutoClick = true;
    document.addEventListener(
      'click',
      (e) => {
        const root = autoRoot(e.target);
        if (!root) return;
        const label = e.target.closest('label');
        const input = label
          ? document.getElementById(label.getAttribute('for'))
          : e.target.closest('input[type="radio"]');
        if (!input || !root.contains(input)) return;
        const state = autoState(root);
        if (!state || state.hidden.includes(input.name)) return;
        const target = autoBest(state, input);
        if (!target) return;
        state.groups.forEach((g, i) => {
          if (!state.hidden.includes(g)) return;
          const radio = Array.from(root.querySelectorAll('input[type="radio"]')).find(
            (r) => r.name === g && r.value === target.options[i]
          );
          if (radio && !radio.checked) radio.checked = true;
        });
      },
      true
    );
  };

  const init = () => {
    setupPrefetch();
    document.querySelectorAll(PICKER_SELECTOR).forEach((root) => {
      // Main PDP form only — leave quick-shop/sticky pickers stock.
      if (!root.closest('product-single')) return;
      if (root.dataset.croVariantUx) return;
      root.dataset.croVariantUx = 'true';
      new CroVariantUX(root);
    });
    autoInit();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Theme editor / dynamic section reloads.
  document.addEventListener('shopify:section:load', init);
})();
